import { MessageBarType } from '@fluentui/react';
import { forwardRef, HTMLAttributes, ReactNode, Ref } from 'react';

import { IComponent, IH2OTheme } from '../../models/components.models';
import { useClassNames, useTheme } from '../../utils/hooks';
import { singleOrArrayToArray } from '../../utils/utils';
import {
  IMessageBarContentClassNames,
  IMessageBarContentStyles,
  messageBarContentStylesDefault,
} from './MessageBar.styles';

export interface IMessageBarContentProps extends IComponent<IMessageBarContentStyles>, HTMLAttributes<HTMLDivElement> {
  title?: string;
  subTitle?: string;
  read?: boolean;
  headerSecondContent?: ReactNode;
  messageBarType?: MessageBarType;
  children?: ReactNode;
}

const getComponentStyles = (theme: IH2OTheme, messageBarType: MessageBarType | undefined): IMessageBarContentStyles => {
  const styles: Partial<IMessageBarContentStyles> = {};
  switch (messageBarType) {
    case MessageBarType.success:
      styles.body = { color: theme.semanticColors?.messageBarContentTextSuccess };
      break;
    case MessageBarType.info:
      styles.body = { color: theme.semanticColors?.messageBarContentTextInfo };
      break;
    case MessageBarType.blocked:
    case MessageBarType.severeWarning:
    case MessageBarType.error:
      styles.body = { color: theme.semanticColors?.messageBarContentTextError };
      break;
    case MessageBarType.warning:
      styles.body = { color: theme.semanticColors?.messageBarContentTextWarning };
      break;
  }
  return { ...styles };
};

export const MessageBarContent = forwardRef((props: IMessageBarContentProps, ref: Ref<HTMLDivElement>) => {
  const { className, styles, children, title, subTitle, messageBarType, headerSecondContent, read, ...elementProps } =
    props;
  const theme = useTheme();
  const classNames = useClassNames<IMessageBarContentStyles, IMessageBarContentClassNames>(
    'MessageBarContent',
    messageBarContentStylesDefault,
    [getComponentStyles(theme, messageBarType), ...singleOrArrayToArray(styles)],
    className,
    read && 'read'
  );
  return (
    <div
      {...elementProps}
      ref={ref}
      className={classNames.root}
      data-test={props['data-test'] || 'message-bar-content'}
    >
      <div className={classNames.header}>
        <div className={classNames.headerContent}>
          {title && <div className={classNames.title}>{title}</div>}
          {subTitle && <div className={classNames.subTitle}>{subTitle}</div>}
        </div>
        {headerSecondContent && <div className={classNames.headerSecondContent}>{headerSecondContent}</div>}
      </div>
      <div className={classNames.body}>{children}</div>
    </div>
  );
});
